import { ApiError } from "@/lib/api";
import type { AuthedRequester } from "@/lib/brokerAuth";
import { BOND_KINDS, bondKindLabel } from "@/lib/bondStateModel";
import type { InvoiceBondRef, InvoiceDetail } from "@/lib/invoices";
import { sentenceCaseKey } from "@/lib/listFormat";

// BRK-S411, BRK-S445 — the Surety account's BILLING tab: the account's
// billing documents — invoices, payment receipts, statements and credit memos
// — from ONE read PMS filters, searches and pages:
//
//   GET /api/broker/accounts/{id}/billing-documents
//       ?year=&kind=&relatedTo=account|SB-#####&q=&limit=&offset=
//
// The route, its parameters and every field below are PMS-S854;
// `test/lib/accountBillingContract.test.ts` fails the day the pin publishes
// them. Nothing about payment beyond the broker-safe status is carried: no
// method, no reference, no payment type (D5, BRK-S187).

/** The billing-document kinds, in the wireframe's order.
 *  PMS-S854 — `documents[].kind`. */
export const BILLING_KINDS = [
  "invoice",
  "receipt",
  "statement",
  "credit_memo",
] as const;
export type BillingKind = (typeof BILLING_KINDS)[number];

const KIND_LABELS: Record<BillingKind, string> = {
  invoice: "Invoice",
  receipt: "Receipt",
  statement: "Statement",
  credit_memo: "Credit memo",
};

/** A kind in words; one this build has not adopted reads "Billing document",
 *  never blank. */
export function billingKindLabel(raw: unknown): string {
  const kind = BILLING_KINDS.find((k) => k === raw);
  return kind === undefined ? "Billing document" : KIND_LABELS[kind];
}

/** Whether an invoice has been paid. Only invoices carry one; every other
 *  kind is null. PMS-S854 — `documents[].paymentStatus`. */
export const PAYMENT_STATUSES = ["not_paid", "paid"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/** The status in words, its tone, or null for a row with none ("—"). An
 *  unknown value reads neutrally. */
export function paymentStatusDisplay(
  raw: unknown,
): { label: string; tone: "danger" | "success" | "neutral" } | null {
  if (raw === null || raw === undefined) return null;
  if (raw === "paid") return { label: "Paid", tone: "success" };
  if (raw === "not_paid") return { label: "Not paid", tone: "danger" };
  return { label: "Status unavailable", tone: "neutral" };
}

/** One billing document. PMS-S854 — every field. */
export interface BillingDocumentRow {
  id: string;
  /** PMS's number for it: INV-, RCT-, STM-, CM-. */
  documentNumber: string;
  kind: BillingKind;
  /** "Premium invoice: P&P bond", "Annual account statement 2026" — PMS's. */
  name: string;
  /** The submission it bills, or null for the account's own (a statement). */
  relatedTo: { sbNumber: string; submissionId: string } | null;
  /** ISO `YYYY-MM-DD`. */
  createdOn: string;
  paymentStatus: PaymentStatus | null;
  /** The file, through the account's document route. */
  documentId: string;
  /** An invoice's own page (`/surety/invoices/{id}`); null for other kinds. */
  invoiceId: string | null;
}

export interface BillingOptionWire {
  value: string;
  label: string;
}

export interface BillingDocumentsPage {
  documents: BillingDocumentRow[];
  total: number;
  limit: number;
  offset: number;
  /** Year and related-to choices over the WHOLE set whatever the request
   *  narrowed (the kinds are the closed `BILLING_KINDS`). */
  facets: { years: string[]; related: BillingOptionWire[] };
}

export interface BillingDocumentsQuery {
  year?: string;
  kind?: string;
  relatedTo?: string;
  q?: string;
  limit: number;
  offset: number;
}

/** GET /api/broker/accounts/{id}/billing-documents — one page of them. */
export function listAccountBillingDocuments(
  request: AuthedRequester,
  accountId: string,
  query: BillingDocumentsQuery,
): Promise<BillingDocumentsPage> {
  const params = new URLSearchParams();
  for (const key of ["year", "kind", "relatedTo", "q"] as const) {
    const value = query[key];
    if (value) params.set(key, value);
  }
  params.set("limit", String(query.limit));
  params.set("offset", String(query.offset));
  return request<BillingDocumentsPage>(
    `/api/broker/accounts/${encodeURIComponent(accountId)}/billing-documents?${params.toString()}`,
  );
}

// The submission page's Billing tab (BRK-S426) still lists invoices from the
// account's invoices read, and names and downloads them with these two.

/** The invoice's name from the bonds it bills: a Performance and Payment pair
 *  is one "P&P bond", as the broker orders it; any other kind is labelled, and
 *  one this build has not heard of is humanised, never dropped. */
export function premiumInvoiceName(bonds: readonly InvoiceBondRef[]): string {
  const kinds = [...new Set(bonds.map((b) => b.kind))];
  const pair = kinds.includes("performance") && kinds.includes("payment");
  const labels = [
    ...(pair ? ["P&P bond"] : []),
    ...kinds
      .filter((k) => !(pair && (k === "performance" || k === "payment")))
      .map((k) =>
        (BOND_KINDS as readonly string[]).includes(k)
          ? bondKindLabel(k as (typeof BOND_KINDS)[number])
          : `${sentenceCaseKey(k)} bond`,
      ),
  ];
  return labels.length > 0
    ? `Premium invoice: ${labels.join(", ")}`
    : "Premium invoice";
}

/**
 * The invoice's own PDF among its documents (the list row carries no
 * `documentId`, so the row's Download reads the detail first). An invoice with
 * none yet is a handled error the row says inline, never a silent no-op.
 */
export function invoiceDocumentIdOf(
  detail: Pick<InvoiceDetail, "documents">,
): string {
  const doc = (detail.documents ?? []).find((d) => d.type === "invoice");
  if (!doc) {
    throw new ApiError(200, "This invoice’s document isn’t ready yet.", detail);
  }
  return doc.documentId;
}
